import { useEffect, useMemo } from "react";
import { addMonths } from "date-fns";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { cn, formatDate, parseDateParam } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Invoice } from "@/lib/types";
import { Mixpanel } from "@/lib/mixpanel";
import SelectingBoatButton from "../../components/select-vessel-button";
import AddInvoiceButton from "../../invoices/components/add-invoice-button";
import { useHasPermission } from "@/hooks/use-permission";
import { PERMISSIONS } from "@/lib/constants/permissions";

interface HeaderProps {
  invoices: Invoice[];
  isDateSelected: boolean;
  setIsDateSelected: (value: boolean) => void;
}

const Header = ({
  invoices,
  isDateSelected,
  setIsDateSelected,
}: HeaderProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchDate = searchParams.get("date");
  const date = useMemo(() => parseDateParam(searchDate), [searchDate]);
  const canWriteExpenses = useHasPermission(PERMISSIONS.Expenses.Write);

  useEffect(() => {
    if (!searchParams.has("date")) {
      setSearchParams((prev) => {
        prev.set("date", formatDate(date, "yyyy-MM-dd"));
        return prev;
      });
    }
  }, [date, searchParams, setSearchParams]);

  const handleMonthChange = (increment: number) => {
    setSearchParams((prev) => {
      const newDate = addMonths(parseDateParam(prev.get("date")), increment);
      prev.set("date", formatDate(newDate, "yyyy-MM-dd"));
      return prev;
    });
  };

  const handleCalendarSelect = (value: Date | undefined) => {
    setSearchParams((prev) => {
      if (value) {
        prev.set("date", formatDate(value, "yyyy-MM-dd"));
      }
      return prev;
    });
    setIsDateSelected(!!value);
  };

  return (
    <div className="flex flex-col items-center justify-center px-5">
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <SelectingBoatButton />
        {canWriteExpenses && (
          <AddInvoiceButton defaultDate={isDateSelected ? date : undefined} />
        )}
      </div>

      <div className="mt-[15px] flex w-full flex-col items-center justify-center">
        <button
          data-testid="calendar-button"
          onClick={() => {
            navigate("/invoices");
            Mixpanel.track("Navigate to Invoices");
          }}
          className={cn(
            "invoice-calendar-button flex w-full items-center justify-between rounded-md border border-y-white/10 border-l-[#406f7e] border-r-[#63919f] text-left font-normal shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25)]",
            !date && "text-muted-foreground"
          )}
        >
          <div
            data-testid="previous-month-button"
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              handleMonthChange(-1);
              Mixpanel.track("Previous Month Button Clicked");
            }}
            className="flex h-9 items-center justify-center px-2 text-white/65 transition-all duration-200 hover:bg-transparent hover:text-white"
          >
            <CaretLeft />
          </div>
          <span
            data-testid="selected-month-value"
            className="text-base font-medium leading-4 text-white"
          >
            {formatDate(date, "MMMM yyyy")}
          </span>
          <div
            data-testid="next-month-button"
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              handleMonthChange(1);
              Mixpanel.track("Next Month Button Clicked");
            }}
            className="flex h-9 items-center justify-center px-2 text-white/65 transition-all duration-200 hover:bg-transparent hover:text-white"
          >
            <CaretRight />
          </div>
        </button>

        <Calendar
          className="mt-[5px] w-full rounded-lg border-[1.5px] border-white/[4%] p-[10px] shadow-[0px_1px_4px_0px_#00000040]"
          onMonthChange={(date) =>
            date &&
            setSearchParams((prev) => {
              prev.set("date", formatDate(date, "yyyy-MM-dd"));
              return prev;
            })
          }
          month={date}
          dayFooter={(date) => {
            const hasInvoice = invoices.some((item) => {
              const invoiceDate = new Date(item.issueDate);
              return (
                formatDate(invoiceDate, "yyyy-MM-dd") ===
                formatDate(date, "yyyy-MM-dd")
              );
            });
            return hasInvoice ? (
              <div
                className="mt-1 flex flex-row items-center justify-center gap-1"
                data-testid={`calendar-day-dot-${formatDate(date, "yyyy-MM-dd")}`}
              >
                <span className="flex h-1 w-1 min-w-1 rounded-full bg-[#FB600D]" />
                <span className="flex h-1 w-1 min-w-1 rounded-full bg-[#26C2DD]" />
              </div>
            ) : null;
          }}
          hideCaption
          mode="single"
          selected={isDateSelected ? date : undefined}
          onSelect={handleCalendarSelect}
        />
      </div>
    </div>
  );
};

export default Header;
